import * as React from "react"
import {StatelessComponent} from "react"
import * as ReactDOM from "react-dom"
import * as Ramda from "ramda"
import {CallJs} from "../lib/types";
import {connect, Store} from "react-redux";
import {createStore, Dispatch} from "redux";

export type Option = {
	key: string
	displayName: string
	selected: boolean
}

export type ValueParams = {
	id?: string
	name: string
	readOnly: boolean
	options: Option[]
	label?: string
	labelWidth?: string
	labelAlign?: string
	highlight: string
	required?: string
	cellspacing?: string|number
	cellpadding?: string|number
	width?: string|number
	displayLimit?: number
}

export type CallbackParams = {
	onDeselect: CheckboxClickCallback
	onSelect: CheckboxClickCallback
}

const ReadOnlyMultiCheckbox = (params: ValueParams) =>
	<div>
		{params.options.map(option => option.selected && <li key={option.key}>{option.displayName}</li>)}
	</div>

const EditableMultiCheckbox = (params: ValueParams & CallbackParams) =>
	params.label ? <LabelledEditableMultiCheckbox {...params}/> : <UnlabelledEditableMultiCheckbox {...params} />

const UnlabelledEditableMultiCheckbox = (params: ValueParams & CallbackParams) =>
	<div>
		<input type="hidden" name={params.name} id={params.id || undefined} value=''/>
		<table cellPadding={params.cellpadding} cellSpacing={params.cellspacing} style={{width: params.width}}><tbody>
			{Ramda.splitEvery(params.displayLimit || 1, params.options).map((l, i) => <tr key={`tr-${i}`}>{l.map((option, idx) => {
				const key = option.key
				const rowParams = {
					onClick: option.selected ? (k: string) => params.onDeselect(k) : (k: string) => params.onSelect(k),
					idx: i*(params.displayLimit || 0) + idx,
					required: params.required,
					name: params.name,
					highlight: params.highlight,
					option: option
				}
				return [
					<RowA key={`rowA-${idx}-${i}`} {...rowParams}/>,
					<RowB key={`rowB-${idx}-${i}`} {...rowParams}/>
				]})}
				{params.options.length % (params.displayLimit || 0) > 1 && <td colSpan={params.options.length % (params.displayLimit||0)}/>}
				</tr>)}
		</tbody></table>
	</div>

type CheckboxClickCallback = (value: string) => void

type RowOptions = {option: Option, required?: string, name: string, highlight: string, idx: number, onClick: (key: string) => void}

const RowB = (params: RowOptions) => {
	const style = Object.assign({cursor: 'pointer'}, params.option.selected ? {background: params.highlight} : {})
	return <td id={makeName(params, "right")} style={style}>
		<label htmlFor={makeName(params, 'checkbox')}>{params.option.displayName}</label>
	</td>
}

const RowA: StatelessComponent<RowOptions> = (params: RowOptions) => {
	const style = Object.assign({width: 20}, params.option.selected ? {background: params.highlight} : undefined)
	return <td id={makeName(params, 'left')} style={style}>
		<input
			onClick={() => params.onClick(params.option.key)}
			type="checkbox"
			name={`${params.name}[]`}
			value={params.option.key}
			title={params.required}
			id={makeName(params, "checkbox")}
			checked={params.option.selected}
		/>
	</td>
}

const makeName = (params: RowOptions, suffix: string) => `highlight_${params.name}_${params.idx}_${suffix}`

const LabelledEditableMultiCheckbox = (params: ValueParams & CallbackParams) =>
	<table>
		<tr>
			<th style={{textAlign: params.labelAlign || 'right', width: params.labelWidth}}>{params.label}</th>
			<td><UnlabelledEditableMultiCheckbox {...params}/></td>
		</tr>
	</table>

export const Multicheckbox = (params: ValueParams & CallbackParams) =>
	params.readOnly ? <ReadOnlyMultiCheckbox {...params}/> : <EditableMultiCheckbox {...params}/>

type State = ValueParams
type Action = {type: "add selected", key: string} | {type: "remove selected", key: string} | {type: "this is not expected to be instantiated"}

const reducer = (store: State, action: Action): State => {
	if(!action)
		return store
	switch (action.type) {
		case "add selected":
			const addOptions = store.options.map(v => v.key === action.key ? Object.assign({}, v, {selected: true}) : v)
			return Object.assign({}, store, {options: addOptions})
		case "remove selected":
			const removeOptions = store.options.map(v => v.key === action.key ? Object.assign({}, v, {selected: false}) : v)
			return Object.assign({}, store, {options: removeOptions})
	}
	return store
}

const mapDispatchToProps = (dispatch: Dispatch<State>): CallbackParams => ({
	onDeselect: (a: string|number) => {if (a === 0) throw a; dispatch({type: "remove selected", key: a})},
	onSelect: (a: string) => dispatch({type: "add selected", key: a})
})

type OwnProps = {store: Store<State>}

export const MulticheckboxCallJs: CallJs<ValueParams> = (replace, params) => {
	const div = document.createElement('div')
	replace(div)

	const store = createStore(reducer, params)
	const Widget = connect<ValueParams, CallbackParams, OwnProps, ValueParams>(a => a, mapDispatchToProps)(Multicheckbox)

	ReactDOM.render(<Widget store={store}/>, div)
}



// WEBPACK FOOTER //
// ./src/legacy/html-input-multi-checkbox.tsx